import type { SceneDef } from "../../genesis/schema";

/**
 * Les Voyages Missionnaires de Paul (jeu 5) — Philippes, la prison à minuit.
 * Ancrage biblique : Actes 16:11-34 (+ Philippiens 4:4, lettre envoyée plus
 * tard à cette même ville). Mécanique signature : à minuit, maintenir la
 * LOUANGE (lever_action, Ac 16:25) déclenche le séisme qui OUVRE les portes
 * (gates, Ac 16:26) ; les chaînes rompues tombent (pickups, Ac 16:26) ; on
 * apporte la LUMIÈRE au geôlier (drag_drop des lampes, Ac 16:29) ; dialogue
 * avec le geôlier (« Que faut-il faire pour être sauvé ? », Ac 16:30), avec
 * Silas, avec Lydie au bord de la rivière (Ac 16:13-14) et avec Luc.
 * VERSION DENSE (vague 2) : 49 interactables étalés sur tout le terrain —
 * prison au nord, forum à l'ouest, maison du geôlier à l'est, rivière
 * Gangitès et Lydie au sud-ouest, Via Egnatia au sud-est. Rendu 100 % data.
 *
 * Interactables « avançants » (pickup + drag_drop + lever_action +
 * guide_target) : 34
 *   = 18 pickups (8 chaînes, 5 fragments du verset Ac 16:31, 2 ceps brisés,
 *     3 rouleaux de cantiques)
 *   + 11 drag_drop (4 lampes → geôlier, 4 mets → table, 3 pourpres → Lydie)
 *   + 4 lever_action (louange de Paul, hymne de Silas, prière au bord de
 *     l'eau, lavage des plaies)
 *   + 1 guide_target (la famille du geôlier vers le lieu du baptême).
 * La difficulté D/A/E scale goalCount sur ce total réel (11 / 23 / 34).
 */
export const voyagesDePaul: SceneDef = {
  id: "voyages-de-paul/scene-1",
  game: "voyages-de-paul",
  title: {
    fr: "Les Voyages Missionnaires de Paul — La Prison de Philippes",
    en: "Paul's Missionary Journeys — The Philippian Jail",
  },
  order: 5,
  keyName: { fr: "Clé de la Délivrance", en: "Key of Deliverance" },
  verseBrand: "Matthieu 16:19",
  env: {
    groundSize: 56,
    skyColor: "#151a2e",
    groundColor: "#3a3630",
    lighting: "night",
    props: [
      // Rempart nord de la prison (blocs massifs irréguliers)
      { id: "prison_wall", shape: "box", pos: [0, 0, -25], color: "#4a453d", size: 4.5, scatter: 8, scatterRadius: 18 },
      // Colonnade du forum de Philippes, colonie romaine
      { id: "colonnade", shape: "cylinder", pos: [-18, 0, -6], color: "#8c8578", size: 2.4, scatter: 10, scatterRadius: 14 },
      // Torches murales qui veillent dans la nuit
      { id: "torches", shape: "flame", pos: [12, 0, -8], color: "#e8933a", size: 1, scatter: 8, scatterRadius: 14 },
      // Toits de la ville endormie au loin
      { id: "philippi_roofs", shape: "box", pos: [22, 0, -20], color: "#2a2f45", size: 2, scatter: 16, scatterRadius: 16 },
      // Oliveraie sur la colline, hors des murs
      { id: "olive_trees", shape: "tree", pos: [-22, 0, -18], color: "#44543a", size: 1.6, scatter: 14, scatterRadius: 12 },
      // Ciel de minuit : étoiles au-dessus de la prison
      { id: "midnight_stars", shape: "flame", pos: [0, 16, -10], color: "#f4f1ff", size: 0.4, scatter: 24, scatterRadius: 26 },
      // Paillasses du cachot intérieur
      { id: "straw_mats", shape: "box", pos: [0, 0, -21], color: "#6e5f43", size: 0.9, scatter: 6, scatterRadius: 6 },
      // La rivière Gangitès, où Lydie écouta Paul (Ac 16:13)
      { id: "riviere_gangites", shape: "water", pos: [-22, 0, 25], color: "#28405e", size: 3, scatter: 12, scatterRadius: 9 },
      // Roseaux au bord de l'eau
      { id: "roseaux", shape: "tree", pos: [-17, 0, 24], color: "#5d6e3f", size: 0.8, scatter: 12, scatterRadius: 7 },
      // Contreforts du mont Orbélos au levant
      { id: "collines", shape: "mountain", pos: [25, 0, -25], color: "#232838", size: 6, scatter: 5, scatterRadius: 12 },
      // Fleurs nocturnes pâles le long des chemins
      { id: "fleurs_nuit", shape: "sphere", pos: [0, 0, 16], color: "#c9c2e8", size: 0.3, scatter: 20, scatterRadius: 18 },
      // Caisses et étals du marché du forum, fermés pour la nuit
      { id: "etals_forum", shape: "box", pos: [-20, 0, 0], color: "#5a4f3c", size: 1.1, scatter: 10, scatterRadius: 8 },
      // Cyprès bordant la Via Egnatia, la grande route romaine
      { id: "cypres_egnatia", shape: "tree", pos: [18, 0, 20], color: "#2f4432", size: 2, scatter: 12, scatterRadius: 12 },
      // Lanternes de la ville, points chauds dans la nuit
      { id: "lanternes_ville", shape: "flame", pos: [16, 0, 12], color: "#e0a44a", size: 0.5, scatter: 10, scatterRadius: 16 },
    ],
  },
  spawn: [0, 0, 10],
  difficulty: {
    // goalCount = interactables avançants accomplis (34 au total :
    // 18 pickups + 11 drag_drop + 4 lever_action + 1 guide_target).
    D: { goalCount: 11, hints: true },
    A: { goalCount: 23, hints: true },
    E: { goalCount: 34, hints: false, timerS: 420 },
  },
  interactables: [
    // ——— Louange : mécanique signature (maintenir → séisme) ———
    {
      id: "praise",
      type: "lever_action",
      label: { fr: "Louange de Paul (maintiens longuement pour chanter à minuit)", en: "Paul's praise (hold long to sing at midnight)" },
      pos: [0, 0, 2],
      shape: "person",
      color: "#e8c14a",
      size: 1.4,
      holdS: 4,
      count: 1,
    },
    {
      id: "silas_hymn",
      type: "lever_action",
      label: { fr: "Hymne de Silas — joins ta voix à la sienne (Ac 16:25)", en: "Silas' hymn — join your voice to his (Acts 16:25)" },
      pos: [-3, 0, 0],
      shape: "person",
      color: "#d4b054",
      size: 1.3,
      holdS: 3,
      count: 1,
    },
    {
      id: "river_prayer",
      type: "lever_action",
      label: { fr: "Lieu de prière au bord de l'eau — recueille-toi (Ac 16:13)", en: "Riverside place of prayer — be still and pray (Acts 16:13)" },
      pos: [-20, 0, 18],
      shape: "stele",
      color: "#9fb4d4",
      size: 1.2,
      holdS: 3,
      count: 1,
    },
    {
      id: "wash_wounds",
      type: "lever_action",
      label: { fr: "Bassine du geôlier — lave les plaies de Paul et Silas (Ac 16:33)", en: "The jailer's basin — wash Paul and Silas' wounds (Acts 16:33)" },
      pos: [13, 0, 1],
      shape: "cylinder",
      color: "#7d9bc4",
      size: 0.9,
      holdS: 3,
      count: 1,
    },
    // ——— Personnages nommés (npc_dialog) ———
    {
      id: "silas",
      type: "npc_dialog",
      label: { fr: "Silas : « Chantons à Dieu, même dans les chaînes » (Ac 16:25)", en: "Silas: \"Let us sing to God, even in chains\" (Acts 16:25)" },
      pos: [-2.5, 0, 2.5],
      shape: "person",
      color: "#7d9bc4",
      size: 1.35,
    },
    {
      id: "jailer",
      type: "npc_dialog",
      label: { fr: "Le geôlier : « Que faut-il que je fasse pour être sauvé ? » (Ac 16:30-31)", en: "The jailer: \"What must I do to be saved?\" (Acts 16:30-31)" },
      pos: [9, 0, -3],
      shape: "person",
      color: "#b5824a",
      size: 1.5,
    },
    {
      id: "lydia",
      type: "npc_dialog",
      label: { fr: "Lydie, marchande de pourpre — le Seigneur lui ouvrit le cœur (Ac 16:14)", en: "Lydia, seller of purple — the Lord opened her heart (Acts 16:14)" },
      pos: [-16, 0, 16],
      shape: "person",
      color: "#8e4a8e",
      size: 1.4,
    },
    {
      id: "luke",
      type: "npc_dialog",
      label: { fr: "Luc le médecin : « Nous cherchâmes à partir pour la Macédoine » (Ac 16:10)", en: "Luke the physician: \"We sought to go into Macedonia\" (Acts 16:10)" },
      pos: [6, 0, 14],
      shape: "person",
      color: "#6f8a6b",
      size: 1.35,
    },
    // ——— Portes ouvertes par le séisme (build_slot) ———
    {
      id: "prison_gate",
      type: "build_slot",
      label: { fr: "Les grandes portes de la prison — le séisme les ouvre", en: "The great prison doors — the earthquake opens them" },
      pos: [0, 0, -10],
      shape: "gate",
      color: "#6b6258",
      size: 4,
      targetId: "prison_gate",
    },
    {
      id: "inner_gate",
      type: "build_slot",
      label: { fr: "La porte du cachot intérieur (Ac 16:24)", en: "The inner cell door (Acts 16:24)" },
      pos: [0, 0, -19],
      shape: "gate",
      color: "#57504a",
      size: 3,
      targetId: "inner_gate",
    },
    {
      id: "gate_cell_est",
      type: "build_slot",
      label: { fr: "Grille de la cellule est — TOUTES les portes s'ouvrirent (Ac 16:26)", en: "East cell grate — ALL the doors were opened (Acts 16:26)" },
      pos: [10, 0, -20],
      shape: "gate",
      color: "#5f574f",
      size: 2.4,
      targetId: "gate_cell_est",
    },
    {
      id: "gate_cell_ouest",
      type: "build_slot",
      label: { fr: "Grille de la cellule ouest — TOUTES les portes s'ouvrirent (Ac 16:26)", en: "West cell grate — ALL the doors were opened (Acts 16:26)" },
      pos: [-10, 0, -20],
      shape: "gate",
      color: "#5f574f",
      size: 2.4,
      targetId: "gate_cell_ouest",
    },
    {
      id: "jailer_table",
      type: "build_slot",
      label: { fr: "La table du geôlier — il leur servit à manger (Ac 16:34)", en: "The jailer's table — he set food before them (Acts 16:34)" },
      pos: [15, 0, -2],
      shape: "box",
      color: "#7a5a38",
      size: 1.4,
      targetId: "jailer_table",
    },
    // ——— Lampes : apporter la lumière au geôlier (Ac 16:29) ———
    {
      id: "lamp_1",
      type: "drag_drop",
      label: { fr: "Lampe à huile — apporte la lumière au geôlier (Ac 16:29)", en: "Oil lamp — bring the light to the jailer (Acts 16:29)" },
      pos: [5, 0, 7],
      shape: "flame",
      color: "#e8933a",
      size: 0.7,
      targetId: "jailer",
      count: 1,
    },
    {
      id: "lamp_2",
      type: "drag_drop",
      label: { fr: "Seconde lampe — le geôlier demanda de la lumière", en: "Second lamp — the jailer called for lights" },
      pos: [-7, 0, 8],
      shape: "flame",
      color: "#e8933a",
      size: 0.7,
      targetId: "jailer",
      count: 1,
    },
    {
      id: "lamp_3",
      type: "drag_drop",
      label: { fr: "Lampe du corps de garde — porte-la au geôlier", en: "Guardroom lamp — carry it to the jailer" },
      pos: [3, 0, -6],
      shape: "flame",
      color: "#e07f2e",
      size: 0.7,
      targetId: "jailer",
      count: 1,
    },
    {
      id: "lamp_4",
      type: "drag_drop",
      label: { fr: "Lampe du couloir des cellules — porte-la au geôlier", en: "Cell-corridor lamp — carry it to the jailer" },
      pos: [-4, 0, -8],
      shape: "flame",
      color: "#e07f2e",
      size: 0.7,
      targetId: "jailer",
      count: 1,
    },
    // ——— Le repas de joie : dresser la table (Ac 16:34) ———
    {
      id: "meal_bread",
      type: "drag_drop",
      label: { fr: "Pain frais — pose-le sur la table du geôlier (Ac 16:34)", en: "Fresh bread — set it on the jailer's table (Acts 16:34)" },
      pos: [18, 0, 4],
      shape: "box",
      color: "#d9b26a",
      size: 0.6,
      targetId: "jailer_table",
      count: 1,
    },
    {
      id: "meal_water_jug",
      type: "drag_drop",
      label: { fr: "Cruche d'eau — pose-la sur la table du repas de joie", en: "Water jug — set it on the table of rejoicing" },
      pos: [20, 0, 0],
      shape: "cylinder",
      color: "#a8763e",
      size: 0.7,
      targetId: "jailer_table",
      count: 1,
    },
    {
      id: "meal_figs",
      type: "drag_drop",
      label: { fr: "Corbeille de figues — pour le repas de toute la maison", en: "Basket of figs — for the whole household's meal" },
      pos: [17, 0, -6],
      shape: "sphere",
      color: "#7c5470",
      size: 0.55,
      targetId: "jailer_table",
      count: 1,
    },
    {
      id: "meal_olives",
      type: "drag_drop",
      label: { fr: "Jarre d'olives — il se réjouit avec toute sa famille (Ac 16:34)", en: "Jar of olives — he rejoiced with all his household (Acts 16:34)" },
      pos: [21, 0, -4],
      shape: "cylinder",
      color: "#6b7440",
      size: 0.6,
      targetId: "jailer_table",
      count: 1,
    },
    // ——— Les pourpres de Lydie (Ac 16:14) ———
    {
      id: "purple_1",
      type: "drag_drop",
      label: { fr: "Étoffe de pourpre — rapporte-la à Lydie de Thyatire", en: "Purple cloth — bring it back to Lydia of Thyatira" },
      pos: [-22, 0, 12],
      shape: "box",
      color: "#7b2d6e",
      size: 0.6,
      targetId: "lydia",
      count: 1,
    },
    {
      id: "purple_2",
      type: "drag_drop",
      label: { fr: "Rouleau de pourpre tombé près des roseaux — rapporte-le à Lydie", en: "Purple roll fallen by the reeds — bring it back to Lydia" },
      pos: [-18, 0, 22],
      shape: "box",
      color: "#8e3a80",
      size: 0.6,
      targetId: "lydia",
      count: 1,
    },
    {
      id: "purple_3",
      type: "drag_drop",
      label: { fr: "Ballot de pourpre au gué de la rivière — rapporte-le à Lydie", en: "Purple bundle at the river ford — bring it back to Lydia" },
      pos: [-24, 0, 20],
      shape: "box",
      color: "#6d2361",
      size: 0.6,
      targetId: "lydia",
      count: 1,
    },
    // ——— Chaînes rompues par le séisme : à faire tomber (pickup) ———
    {
      id: "chain_paul",
      type: "pickup",
      label: { fr: "Chaîne de Paul — rompue, fais-la tomber", en: "Paul's chain — broken, let it fall" },
      pos: [-1.5, 0, 4.5],
      shape: "capsule",
      color: "#8a8a92",
      size: 0.6,
    },
    {
      id: "chain_silas",
      type: "pickup",
      label: { fr: "Chaîne de Silas — rompue, fais-la tomber", en: "Silas' chain — broken, let it fall" },
      pos: [-5, 0, 3],
      shape: "capsule",
      color: "#8a8a92",
      size: 0.6,
    },
    {
      id: "chain_est_1",
      type: "pickup",
      label: { fr: "Chaîne de la cellule est — les liens de TOUS furent rompus", en: "East cell chain — EVERYONE's bonds were loosened" },
      pos: [13, 0, -14],
      shape: "capsule",
      color: "#8a8a92",
      size: 0.6,
    },
    {
      id: "chain_est_2",
      type: "pickup",
      label: { fr: "Seconde chaîne de la cellule est — tombée au pied du mur", en: "Second east cell chain — fallen at the foot of the wall" },
      pos: [16, 0, -17],
      shape: "capsule",
      color: "#7e7e88",
      size: 0.6,
    },
    {
      id: "chain_ouest_1",
      type: "pickup",
      label: { fr: "Chaîne de la cellule ouest — les liens de TOUS furent rompus", en: "West cell chain — EVERYONE's bonds were loosened" },
      pos: [-13, 0, -14],
      shape: "capsule",
      color: "#8a8a92",
      size: 0.6,
    },
    {
      id: "chain_ouest_2",
      type: "pickup",
      label: { fr: "Seconde chaîne de la cellule ouest — brisée net par le séisme", en: "Second west cell chain — snapped clean by the earthquake" },
      pos: [-16, 0, -17],
      shape: "capsule",
      color: "#7e7e88",
      size: 0.6,
    },
    {
      id: "chain_fond_1",
      type: "pickup",
      label: { fr: "Chaîne du cachot du fond — même la plus profonde est rompue", en: "Back-dungeon chain — even the deepest one is broken" },
      pos: [-3, 0, -23],
      shape: "capsule",
      color: "#767680",
      size: 0.6,
    },
    {
      id: "chain_fond_2",
      type: "pickup",
      label: { fr: "Chaîne scellée du cachot du fond — arrachée de la muraille", en: "Bolted back-dungeon chain — torn from the wall" },
      pos: [3, 0, -23],
      shape: "capsule",
      color: "#767680",
      size: 0.6,
    },
    // ——— Fragments du verset Actes 16:31 (pickup) ———
    {
      id: "versefrag_1",
      type: "pickup",
      label: { fr: "Fragment du verset : « Crois » (Ac 16:31)", en: "Verse fragment: \"Believe\" (Acts 16:31)" },
      pos: [-10, 0, 12],
      shape: "sphere",
      color: "#f0e3b0",
      size: 0.5,
    },
    {
      id: "versefrag_2",
      type: "pickup",
      label: { fr: "Fragment du verset : « au Seigneur Jésus » (Ac 16:31)", en: "Verse fragment: \"in the Lord Jesus\" (Acts 16:31)" },
      pos: [10, 0, 10],
      shape: "sphere",
      color: "#f0e3b0",
      size: 0.5,
    },
    {
      id: "versefrag_3",
      type: "pickup",
      label: { fr: "Fragment du verset : « et tu seras sauvé » (Ac 16:31)", en: "Verse fragment: \"and you will be saved\" (Acts 16:31)" },
      pos: [0, 0, 20],
      shape: "sphere",
      color: "#f0e3b0",
      size: 0.5,
    },
    {
      id: "versefrag_4",
      type: "pickup",
      label: { fr: "Fragment du verset : « toi » (Ac 16:31)", en: "Verse fragment: \"you\" (Acts 16:31)" },
      pos: [-14, 0, 2],
      shape: "sphere",
      color: "#f0e3b0",
      size: 0.5,
    },
    {
      id: "versefrag_5",
      type: "pickup",
      label: { fr: "Fragment du verset : « et ta famille » (Ac 16:31)", en: "Verse fragment: \"and your household\" (Acts 16:31)" },
      pos: [14, 0, 10],
      shape: "sphere",
      color: "#f0e3b0",
      size: 0.5,
    },
    // ——— Ceps brisés (Ac 16:24) ———
    {
      id: "ceps_1",
      type: "pickup",
      label: { fr: "Cep de bois brisé — leurs pieds y étaient serrés (Ac 16:24)", en: "Broken wooden stock — their feet were fastened in it (Acts 16:24)" },
      pos: [-2, 0, -16],
      shape: "box",
      color: "#6e5a3e",
      size: 0.7,
    },
    {
      id: "ceps_2",
      type: "pickup",
      label: { fr: "Second cep fendu par le séisme — ramasse-le", en: "Second stock split by the earthquake — pick it up" },
      pos: [2, 0, -14],
      shape: "box",
      color: "#6e5a3e",
      size: 0.7,
    },
    // ——— Rouleaux de cantiques (pickup) ———
    {
      id: "scroll_1",
      type: "pickup",
      label: { fr: "Rouleau de cantique — un psaume chanté dans la nuit", en: "Hymn scroll — a psalm sung in the night" },
      pos: [-8, 0, -4],
      shape: "cylinder",
      color: "#cdb98a",
      size: 0.5,
    },
    {
      id: "scroll_2",
      type: "pickup",
      label: { fr: "Rouleau de louange — les prisonniers écoutaient (Ac 16:25)", en: "Praise scroll — the prisoners were listening (Acts 16:25)" },
      pos: [7, 0, 2],
      shape: "cylinder",
      color: "#cdb98a",
      size: 0.5,
    },
    {
      id: "scroll_3",
      type: "pickup",
      label: { fr: "Rouleau de prière — porté depuis le bord de la rivière", en: "Prayer scroll — carried from the riverside" },
      pos: [-11, 0, 6],
      shape: "cylinder",
      color: "#cdb98a",
      size: 0.5,
    },
    // ——— La famille du geôlier : conduite au lieu du baptême ———
    {
      id: "jailer_household",
      type: "guide_target",
      label: { fr: "La famille du geôlier — conduis-la au bord de l'eau (Ac 16:33-34)", en: "The jailer's household — lead them to the waterside (Acts 16:33-34)" },
      pos: [12, 0, 6],
      shape: "person",
      color: "#c4a06a",
      size: 1.2,
      targetId: "river_baptism",
      count: 1,
    },
    // ——— Panneaux bibliques (stèles) ———
    {
      id: "panel_ac16_25",
      type: "panel",
      label: { fr: "Lire : à minuit, la louange", en: "Read: at midnight, praise" },
      pos: [-8, 0, 4],
      shape: "stele",
      color: "#b9a06a",
      size: 1.4,
      panelId: "p_ac16_25",
    },
    {
      id: "panel_ac16_31",
      type: "panel",
      label: { fr: "Lire : crois au Seigneur Jésus", en: "Read: believe in the Lord Jesus" },
      pos: [8, 0, 4],
      shape: "stele",
      color: "#b9a06a",
      size: 1.4,
      panelId: "p_ac16_31",
    },
    {
      id: "panel_ac16_26",
      type: "panel",
      label: { fr: "Cachot du fond — fondements ébranlés, portes ouvertes", en: "Back cell — foundations shaken, doors opened" },
      pos: [0, 0, -24],
      shape: "stele",
      color: "#7a7268",
      size: 1.6,
      panelId: "p_ac16_26",
    },
    {
      id: "panel_ac16_14",
      type: "panel",
      label: { fr: "Lire : au bord de la rivière, le cœur de Lydie s'ouvre", en: "Read: by the river, Lydia's heart opens" },
      pos: [-19, 0, 13],
      shape: "stele",
      color: "#a88bb0",
      size: 1.4,
      panelId: "p_ac16_14",
    },
    {
      id: "river_baptism",
      type: "panel",
      label: { fr: "Le gué du baptême — il fut baptisé, lui et tous les siens (Ac 16:33)", en: "The baptism ford — he was baptized, he and all his household (Acts 16:33)" },
      pos: [-24, 0, 8],
      shape: "water",
      color: "#3e5f8a",
      size: 1.8,
      panelId: "p_ac16_34",
    },
    {
      id: "panel_php4_4",
      type: "panel",
      label: { fr: "Borne de la Via Egnatia — « Réjouissez-vous toujours »", en: "Via Egnatia milestone — \"Rejoice always\"" },
      pos: [22, 0, 16],
      shape: "stele",
      color: "#b9a06a",
      size: 1.4,
      panelId: "p_php4_4",
    },
  ],
  objectives: [
    {
      id: "obj_praise",
      kind: "survive",
      label: { fr: "Loue Dieu à minuit jusqu'au séisme (Ac 16:25)", en: "Praise God at midnight until the earthquake (Acts 16:25)" },
      target: "praise",
    },
    {
      id: "obj_gate",
      kind: "build",
      label: { fr: "Le séisme ébranle la prison — laisse les portes s'ouvrir (Ac 16:26)", en: "The earthquake shakes the prison — let the doors open (Acts 16:26)" },
      target: "prison_gate",
    },
    {
      id: "obj_chains",
      kind: "collect",
      label: { fr: "Fais tomber les huit chaînes rompues (Ac 16:26)", en: "Let the eight broken chains fall (Acts 16:26)" },
      count: 8,
    },
    {
      id: "obj_light",
      kind: "guide",
      label: { fr: "Apporte les quatre lampes au geôlier (Ac 16:29)", en: "Bring the four lamps to the jailer (Acts 16:29)" },
      target: "jailer",
      count: 4,
    },
    {
      id: "obj_jailer",
      kind: "reach",
      label: { fr: "Réponds au geôlier : « Crois au Seigneur Jésus » (Ac 16:31)", en: "Answer the jailer: \"Believe in the Lord Jesus\" (Acts 16:31)" },
      target: "jailer",
    },
    {
      id: "obj_verse",
      kind: "collect",
      label: { fr: "Rassemble les cinq fragments du verset Actes 16:31", en: "Gather the five fragments of the verse Acts 16:31" },
      count: 5,
    },
    {
      id: "obj_meal",
      kind: "build",
      label: { fr: "Dresse la table du repas de joie chez le geôlier (Ac 16:34)", en: "Set the table of rejoicing at the jailer's house (Acts 16:34)" },
      target: "jailer_table",
      count: 4,
    },
    {
      id: "obj_purple",
      kind: "guide",
      label: { fr: "Rapporte les trois étoffes de pourpre à Lydie (Ac 16:14)", en: "Bring the three purple cloths back to Lydia (Acts 16:14)" },
      target: "lydia",
      count: 3,
    },
    {
      id: "obj_household",
      kind: "guide",
      label: { fr: "Conduis la famille du geôlier au gué du baptême (Ac 16:33)", en: "Lead the jailer's household to the baptism ford (Acts 16:33)" },
      target: "river_baptism",
    },
  ],
  panels: [
    {
      id: "p_ac16_25",
      title: { fr: "Louange à minuit", en: "Praise at midnight" },
      reference: "Actes 16:25",
      passage: {
        fr: "Vers le milieu de la nuit, Paul et Silas priaient et chantaient les louanges de Dieu, et les prisonniers les entendaient.",
        en: "But about midnight Paul and Silas were praying and singing hymns to God, and the prisoners were listening to them.",
      },
      explain: {
        fr: "Battus de verges et les pieds dans les ceps, Paul et Silas ne se plaignent pas : ils chantent. La louange n'attend pas des circonstances faciles, elle transforme la nuit. Et les autres prisonniers écoutent — la louange est déjà un témoignage avant même le miracle.",
        en: "Beaten with rods and their feet in the stocks, Paul and Silas do not complain: they sing. Praise does not wait for easy circumstances; it transforms the night. And the other prisoners listen — praise is already a witness even before the miracle.",
      },
      didYouKnow: {
        fr: "Philippes était une colonie romaine (Ac 16:12) : c'est la première ville d'Europe où l'Évangile fut annoncé, d'abord à Lydie au bord de la rivière (Ac 16:13-14).",
        en: "Philippi was a Roman colony (Acts 16:12): it is the first city in Europe where the Gospel was preached, first to Lydia by the riverside (Acts 16:13-14).",
      },
      application: {
        fr: "Quelle « nuit » peux-tu affronter cette semaine par la louange plutôt que par la plainte ?",
        en: "What 'night' can you face this week with praise rather than complaint?",
      },
    },
    {
      id: "p_ac16_26",
      title: { fr: "Le séisme : portes ouvertes, liens rompus", en: "The earthquake: doors opened, bonds loosened" },
      reference: "Actes 16:26",
      passage: {
        fr: "Tout à coup il se fit un grand tremblement de terre, en sorte que les fondements de la prison furent ébranlés; au même instant, toutes les portes s'ouvrirent, et les liens de tous les prisonniers furent rompus.",
        en: "Suddenly there was a great earthquake, so that the foundations of the prison were shaken; and immediately all the doors were opened, and everyone's bonds were loosened.",
      },
      explain: {
        fr: "Dieu répond à la louange : les fondements tremblent, TOUTES les portes s'ouvrent, les liens de TOUS tombent. Pourtant personne ne s'enfuit — Paul reste, car la vraie liberté n'est pas d'abord celle des portes ouvertes. Le miracle prépare une rencontre : celle du geôlier avec le salut.",
        en: "God answers the praise: the foundations shake, ALL the doors open, EVERYONE's bonds fall. Yet no one flees — Paul stays, for true freedom is not first about open doors. The miracle prepares a meeting: the jailer's meeting with salvation.",
      },
      didYouKnow: {
        fr: "Paul cria : « Ne te fais point de mal, nous sommes tous ici » (Ac 16:28). En restant, les prisonniers sauvèrent la vie du geôlier avant que Dieu ne sauve son âme.",
        en: "Paul cried out, \"Don't harm yourself, for we are all here!\" (Acts 16:28). By staying, the prisoners protected the jailer's life before God saved his soul.",
      },
      application: {
        fr: "Quand Dieu t'ouvre une porte, penses-tu aussi à ceux qui restent autour de toi ?",
        en: "When God opens a door for you, do you also think of those who remain around you?",
      },
    },
    {
      id: "p_ac16_31",
      title: { fr: "Crois, et tu seras sauvé", en: "Believe, and you will be saved" },
      reference: "Actes 16:30-31",
      passage: {
        fr: "il les fit sortir, et dit: Seigneurs, que faut-il que je fasse pour être sauvé? Paul et Silas répondirent: Crois au Seigneur Jésus, et tu seras sauvé, toi et ta famille.",
        en: "and brought them out and said, \"Sirs, what must I do to be saved?\" They said, \"Believe in the Lord Jesus Christ, and you will be saved, you and your household.\"",
      },
      explain: {
        fr: "Le geôlier, tremblant, pose la plus grande des questions. La réponse est simple et libératrice : croire au Seigneur Jésus — ni œuvre à accomplir, ni prix à payer. Cette même nuit, lui et toute sa maison écoutent la Parole, sont baptisés et se réjouissent (Ac 16:32-34).",
        en: "The jailer, trembling, asks the greatest of questions. The answer is simple and freeing: believe in the Lord Jesus — no work to perform, no price to pay. That very night, he and his whole household hear the Word, are baptized, and rejoice (Acts 16:32-34).",
      },
      didYouKnow: {
        fr: "Le geôlier lava leurs plaies avant d'être baptisé (Ac 16:33) : la foi produit aussitôt la compassion concrète.",
        en: "The jailer washed their wounds before being baptized (Acts 16:33): faith at once produces concrete compassion.",
      },
      application: {
        fr: "À qui, dans ton entourage, pourrais-tu annoncer cette bonne nouvelle simple cette semaine ?",
        en: "Who around you could you share this simple good news with this week?",
      },
    },
    {
      id: "p_ac16_14",
      title: { fr: "Lydie : un cœur ouvert au bord de l'eau", en: "Lydia: a heart opened by the riverside" },
      reference: "Actes 16:14",
      passage: {
        fr: "L'une d'elles, nommée Lydie, marchande de pourpre, de la ville de Thyatire, était une femme craignant Dieu, et elle écoutait. Le Seigneur lui ouvrit le cœur, pour qu'elle fût attentive à ce que disait Paul.",
        en: "A certain woman named Lydia, a seller of purple, of the city of Thyatira, one who worshiped God, heard us. The Lord opened her heart to listen to the things which were spoken by Paul.",
      },
      explain: {
        fr: "Avant la prison, il y eut la rivière : un simple lieu de prière hors de la ville (Ac 16:13). Lydie écoute, et c'est le Seigneur lui-même qui ouvre son cœur. Première convertie d'Europe, elle ouvre aussitôt sa maison aux messagers (Ac 16:15) : un cœur ouvert devient une porte ouverte.",
        en: "Before the prison there was the river: a simple place of prayer outside the city (Acts 16:13). Lydia listens, and it is the Lord Himself who opens her heart. The first convert of Europe, she at once opens her home to the messengers (Acts 16:15): an opened heart becomes an open door.",
      },
      didYouKnow: {
        fr: "La pourpre de Thyatire était une teinture de grand prix réservée aux riches et aux dignitaires : Lydie était une femme d'affaires, et sa maison devint la première église d'Europe (Ac 16:40).",
        en: "Thyatira's purple was a costly dye reserved for the wealthy and for dignitaries: Lydia was a businesswoman, and her home became the first church of Europe (Acts 16:40).",
      },
      application: {
        fr: "Écouter avec attention, comme Lydie : quel moment calmes-tu cette semaine pour laisser Dieu parler ?",
        en: "Listening attentively, like Lydia: what quiet moment will you set apart this week to let God speak?",
      },
    },
    {
      id: "p_ac16_34",
      title: { fr: "La joie d'une maison qui a cru", en: "The joy of a household that believed" },
      reference: "Actes 16:33-34",
      passage: {
        fr: "Il les prit avec lui, à cette heure même de la nuit, il lava leurs plaies, et aussitôt il fut baptisé, lui et tous les siens. Les ayant conduits dans son logement, il leur servit à manger, et il se réjouit avec toute sa famille de ce qu'il avait cru en Dieu.",
        en: "He took them the same hour of the night and washed their stripes, and was immediately baptized, he and all his household. He brought them up into his house and set food before them, and rejoiced greatly with all his household, having believed in God.",
      },
      explain: {
        fr: "La nuit qui avait commencé par des coups s'achève par un repas de fête. Le geôlier lave les plaies de ceux qu'il gardait, toute sa maison passe par l'eau du baptême, et la table est dressée. La foi ne reste jamais abstraite : elle lave, elle nourrit, elle réjouit toute une famille.",
        en: "The night that began with beatings ends with a feast. The jailer washes the wounds of the men he was guarding, his whole household passes through the water of baptism, and the table is set. Faith never stays abstract: it washes, it feeds, it fills a whole family with joy.",
      },
      didYouKnow: {
        fr: "Le lendemain, Paul, citoyen romain battu sans jugement, obligea les magistrats à venir eux-mêmes les libérer (Ac 16:37-39) — protégeant ainsi la jeune église de Philippes.",
        en: "The next day Paul, a Roman citizen beaten without trial, made the magistrates come free them in person (Acts 16:37-39) — thereby protecting the young church of Philippi.",
      },
      application: {
        fr: "Quel geste concret — un soin, un repas, un accueil — peut dire ta foi à ta propre maison cette semaine ?",
        en: "What concrete gesture — a kindness, a meal, a welcome — could express your faith to your own household this week?",
      },
    },
    {
      id: "p_php4_4",
      title: { fr: "Réjouissez-vous toujours", en: "Rejoice always" },
      reference: "Philippiens 4:4",
      passage: {
        fr: "Réjouissez-vous toujours dans le Seigneur; je le répète, réjouissez-vous.",
        en: "Rejoice in the Lord always! Again I will say, \"Rejoice!\"",
      },
      explain: {
        fr: "Des années plus tard, Paul écrit aux chrétiens de Philippes — depuis une autre prison. À la ville où il avait chanté à minuit, il redit le même secret : la joie dans le Seigneur ne dépend pas des murs qui nous entourent. Ce qu'il avait vécu dans leur cachot, il en fait un commandement d'espérance.",
        en: "Years later, Paul writes to the Christians of Philippi — from another prison. To the city where he had sung at midnight, he repeats the same secret: joy in the Lord does not depend on the walls around us. What he lived out in their jail, he turns into a command of hope.",
      },
      didYouKnow: {
        fr: "L'épître aux Philippiens est surnommée « l'épître de la joie » : les mots joie et se réjouir y reviennent plus de quinze fois, écrits par un prisonnier.",
        en: "The letter to the Philippians is nicknamed \"the epistle of joy\": the words joy and rejoice appear in it more than fifteen times, written by a prisoner.",
      },
      application: {
        fr: "« Je le répète » : quelle joie du Seigneur as-tu besoin de te redire aujourd'hui, à voix haute ?",
        en: "\"Again I will say\": what joy of the Lord do you need to say to yourself again today, out loud?",
      },
    },
  ],
  quizzes: [
    {
      id: "q_midnight",
      level: "D",
      prompt: { fr: "Que faisaient Paul et Silas à minuit dans la prison ?", en: "What were Paul and Silas doing at midnight in the jail?" },
      choices: [
        { fr: "Ils dormaient profondément", en: "They were fast asleep" },
        { fr: "Ils priaient et chantaient les louanges de Dieu", en: "They were praying and singing hymns to God" },
        { fr: "Ils creusaient un tunnel pour s'enfuir", en: "They were digging a tunnel to escape" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Vers le milieu de la nuit, Paul et Silas priaient et chantaient les louanges de Dieu. » (Actes 16:25) La louange peut monter même depuis un cachot.",
        en: "\"About midnight Paul and Silas were praying and singing hymns to God.\" (Acts 16:25) Praise can rise even from a dungeon.",
      },
    },
    {
      id: "q_saved",
      level: "A",
      prompt: { fr: "Que répondirent Paul et Silas au geôlier qui demandait comment être sauvé ?", en: "What did Paul and Silas answer the jailer who asked how to be saved?" },
      choices: [
        { fr: "« Paie ta dette au temple »", en: "\"Pay your debt to the temple\"" },
        { fr: "« Fuis pendant qu'il en est temps »", en: "\"Flee while there is still time\"" },
        { fr: "« Crois au Seigneur Jésus, et tu seras sauvé »", en: "\"Believe in the Lord Jesus, and you will be saved\"" },
        { fr: "« Accomplis d'abord quarante jours de jeûne »", en: "\"First complete forty days of fasting\"" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Crois au Seigneur Jésus, et tu seras sauvé, toi et ta famille. » (Actes 16:31) Le salut est reçu par la foi, pas acheté par des œuvres — et il rayonne sur toute la maison.",
        en: "\"Believe in the Lord Jesus Christ, and you will be saved, you and your household.\" (Acts 16:31) Salvation is received by faith, not bought by works — and it shines on the whole household.",
      },
    },
    {
      id: "q_why_prison",
      level: "E",
      prompt: {
        fr: "Pourquoi Paul et Silas avaient-ils été jetés en prison à Philippes ?",
        en: "Why had Paul and Silas been thrown into prison at Philippi?",
      },
      choices: [
        { fr: "Ils avaient refusé de payer l'impôt romain", en: "They had refused to pay the Roman tax" },
        { fr: "Ils avaient délivré une servante d'un esprit de divination, et ses maîtres perdaient leur gain", en: "They had freed a servant girl from a spirit of divination, and her masters lost their profit" },
        { fr: "Ils avaient prêché dans le temple sans permission", en: "They had preached in the temple without permission" },
        { fr: "Ils étaient entrés dans la ville sans laissez-passer", en: "They had entered the city without a pass" },
      ],
      answerIndex: 1,
      explain: {
        fr: "Paul chassa l'esprit de divination d'une servante que ses maîtres exploitaient (Ac 16:16-18). Voyant disparaître « l'espérance de leur gain », ils traînèrent Paul et Silas devant les magistrats, qui les firent battre de verges et jeter en prison (Ac 16:19-24). La délivrance d'une seule âme valait ce prix.",
        en: "Paul cast the spirit of divination out of a servant girl her masters were exploiting (Acts 16:16-18). Seeing \"the hope of their gain\" gone, they dragged Paul and Silas before the magistrates, who had them beaten with rods and jailed (Acts 16:19-24). The deliverance of one soul was worth that price.",
      },
    },
    {
      id: "q_lydia",
      level: "A",
      prompt: {
        fr: "Qui fut la première personne convertie à Philippes — et en Europe ?",
        en: "Who was the first person converted at Philippi — and in Europe?",
      },
      choices: [
        { fr: "Le geôlier de la prison", en: "The prison jailer" },
        { fr: "Lydie, marchande de pourpre, au bord de la rivière", en: "Lydia, a seller of purple, by the riverside" },
        { fr: "Un magistrat de la colonie romaine", en: "A magistrate of the Roman colony" },
        { fr: "La servante délivrée de l'esprit de divination", en: "The servant girl freed from the spirit of divination" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Le Seigneur lui ouvrit le cœur » (Actes 16:14). Lydie écoutait Paul au lieu de prière, au bord de la rivière ; elle crut, fut baptisée avec sa famille et accueillit les missionnaires dans sa maison (Ac 16:15).",
        en: "\"The Lord opened her heart\" (Acts 16:14). Lydia was listening to Paul at the place of prayer by the river; she believed, was baptized with her household, and welcomed the missionaries into her home (Acts 16:15).",
      },
    },
    {
      id: "q_stay",
      level: "E",
      prompt: {
        fr: "Quand les portes s'ouvrirent, que cria Paul au geôlier prêt à se tuer ?",
        en: "When the doors opened, what did Paul cry out to the jailer about to kill himself?",
      },
      choices: [
        { fr: "« Fuis avec nous avant l'aube ! »", en: "\"Flee with us before dawn!\"" },
        { fr: "« Ne te fais point de mal, nous sommes tous ici »", en: "\"Don't harm yourself, for we are all here!\"" },
        { fr: "« Ouvre aussi les autres cellules ! »", en: "\"Open the other cells too!\"" },
        { fr: "« Rends-nous nos vêtements et nos sandales »", en: "\"Give us back our cloaks and sandals\"" },
      ],
      answerIndex: 1,
      explain: {
        fr: "Le geôlier, croyant les prisonniers enfuis, allait se tuer ; Paul cria d'une voix forte : « Ne te fais point de mal, nous sommes tous ici. » (Actes 16:27-28) Rester pour sauver son gardien : c'est cette grâce qui ouvrit le chemin du salut de toute sa maison.",
        en: "The jailer, thinking the prisoners had escaped, was about to kill himself; Paul cried with a loud voice, \"Don't harm yourself, for we are all here!\" (Acts 16:27-28) Staying to save their guard: that grace opened the way of salvation for his whole household.",
      },
    },
    {
      id: "qD1",
      level: "D",
      prompt: { fr: "Que demanda le geôlier avant de se précipiter vers Paul et Silas ?", en: "What did the jailer call for before rushing in to Paul and Silas?" },
      choices: [
        { fr: "De la lumière", en: "Lights" },
        { fr: "Une épée neuve", en: "A new sword" },
        { fr: "Les clés des cellules", en: "The keys of the cells" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Alors le geôlier, ayant demandé de la lumière, entra précipitamment, et se jeta tout tremblant aux pieds de Paul et de Silas. » (Actes 16:29) Il cherchait des lampes — et c'est la vraie Lumière qu'il allait rencontrer.",
        en: "\"He called for lights, sprang in, fell down trembling before Paul and Silas.\" (Acts 16:29) He was looking for lamps — and it was the true Light he was about to meet.",
      },
    },
    {
      id: "qD2",
      level: "D",
      prompt: { fr: "Quel métier exerçait Lydie, rencontrée au bord de la rivière ?", en: "What was the trade of Lydia, met by the riverside?" },
      choices: [
        { fr: "Tisserande de lin", en: "Linen weaver" },
        { fr: "Marchande de pourpre", en: "Seller of purple" },
        { fr: "Potière", en: "Potter" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« L'une d'elles, nommée Lydie, marchande de pourpre, de la ville de Thyatire… » (Actes 16:14) La pourpre était une teinture de grand prix : Lydie était une femme d'affaires, que Dieu rejoignit au lieu de prière.",
        en: "\"A certain woman named Lydia, a seller of purple, of the city of Thyatira…\" (Acts 16:14) Purple was a costly dye: Lydia was a businesswoman, whom God met at the place of prayer.",
      },
    },
    {
      id: "qD3",
      level: "D",
      prompt: { fr: "Que produisit le grand tremblement de terre dans la prison ?", en: "What did the great earthquake do inside the prison?" },
      choices: [
        { fr: "Seule la porte de Paul s'ouvrit", en: "Only Paul's door opened" },
        { fr: "Les murailles de la ville s'écroulèrent", en: "The city walls collapsed" },
        { fr: "Toutes les portes s'ouvrirent et les liens de tous furent rompus", en: "All the doors opened and everyone's bonds were loosened" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Au même instant, toutes les portes s'ouvrirent, et les liens de tous les prisonniers furent rompus. » (Actes 16:26) La réponse de Dieu à la louange dépassa Paul et Silas : elle toucha TOUS les prisonniers.",
        en: "\"Immediately all the doors were opened, and everyone's bonds were loosened.\" (Acts 16:26) God's answer to praise went beyond Paul and Silas: it reached ALL the prisoners.",
      },
    },
    {
      id: "qD4",
      level: "D",
      prompt: { fr: "Où se trouvait le lieu de prière où Paul parla à Lydie ?", en: "Where was the place of prayer where Paul spoke to Lydia?" },
      choices: [
        { fr: "Au bord de la rivière, hors de la porte de la ville", en: "By the riverside, outside the city gate" },
        { fr: "Sur la place du forum", en: "On the forum square" },
        { fr: "Dans la grande synagogue de Philippes", en: "In the great synagogue of Philippi" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Le jour du sabbat, nous nous rendîmes, hors de la porte, vers une rivière, où nous pensions que se trouvait un lieu de prière. » (Actes 16:13) La prière se tenait au bord de l'eau — et c'est là que le Seigneur ouvrit le cœur de Lydie (Ac 16:14).",
        en: "\"On the Sabbath day we went forth outside of the city by a riverside, where we supposed there was a place of prayer.\" (Acts 16:13) Prayer was held by the water — and there the Lord opened Lydia's heart (Acts 16:14).",
      },
    },
    {
      id: "qA1",
      level: "A",
      prompt: { fr: "Dans quel ordre ces événements de Philippes se sont-ils déroulés ?", en: "In what order did these events at Philippi take place?" },
      choices: [
        { fr: "La prison, puis Lydie, puis le séisme", en: "The prison, then Lydia, then the earthquake" },
        { fr: "Lydie à la rivière, puis la prison, puis le séisme", en: "Lydia at the river, then the prison, then the earthquake" },
        { fr: "Le séisme, puis la prison, puis Lydie", en: "The earthquake, then the prison, then Lydia" },
      ],
      answerIndex: 1,
      explain: {
        fr: "D'abord la conversion de Lydie au bord de la rivière (Ac 16:13-15), puis la délivrance de la servante et l'emprisonnement (Ac 16:16-24), enfin la louange de minuit et le séisme (Ac 16:25-26). L'Évangile avance d'étape en étape.",
        en: "First Lydia's conversion by the river (Acts 16:13-15), then the servant girl's deliverance and the imprisonment (Acts 16:16-24), finally the midnight praise and the earthquake (Acts 16:25-26). The Gospel advances step by step.",
      },
    },
    {
      id: "qA2",
      level: "A",
      prompt: { fr: "Qu'a fait le geôlier cette même nuit, aussitôt après avoir cru ?", en: "What did the jailer do that very night, as soon as he believed?" },
      choices: [
        { fr: "Il attendit le matin pour libérer Paul et Silas", en: "He waited until morning to release Paul and Silas" },
        { fr: "Il courut prévenir les magistrats", en: "He ran to inform the magistrates" },
        { fr: "Il lava leurs plaies et fut baptisé, lui et tous les siens", en: "He washed their wounds and was baptized, he and all his household" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Il les prit avec lui, à cette heure même de la nuit, il lava leurs plaies, et aussitôt il fut baptisé, lui et tous les siens. » (Actes 16:33) La foi vraie agit sans attendre : elle soigne, puis elle passe par l'eau du baptême.",
        en: "\"He took them the same hour of the night and washed their stripes, and was immediately baptized, he and all his household.\" (Acts 16:33) True faith acts without delay: it tends wounds, then passes through the water of baptism.",
      },
    },
    {
      id: "qA3",
      level: "A",
      prompt: { fr: "Pourquoi Paul et ses compagnons étaient-ils passés en Macédoine ?", en: "Why had Paul and his companions crossed over to Macedonia?" },
      choices: [
        { fr: "Un Macédonien lui apparut en vision : « Passe en Macédoine, secours-nous ! »", en: "A Macedonian appeared to him in a vision: \"Come over into Macedonia and help us!\"" },
        { fr: "Le gouverneur de Philippes les avait invités", en: "The governor of Philippi had invited them" },
        { fr: "Ils fuyaient une famine en Asie", en: "They were fleeing a famine in Asia" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Paul eut de nuit une vision: un Macédonien lui apparut, et lui fit cette prière: Passe en Macédoine, secours-nous! » (Actes 16:9) Aussitôt l'équipe conclut que Dieu l'appelait — et l'Évangile entra en Europe (Ac 16:10).",
        en: "\"A vision appeared to Paul in the night. There was a man of Macedonia standing, begging him and saying, 'Come over into Macedonia and help us.'\" (Acts 16:9) At once the team concluded God was calling them — and the Gospel entered Europe (Acts 16:10).",
      },
    },
    {
      id: "qE1",
      level: "E",
      prompt: { fr: "Le lendemain, quand les magistrats voulurent les relâcher en secret, que fit valoir Paul ?", en: "The next day, when the magistrates wanted to release them quietly, what did Paul assert?" },
      choices: [
        { fr: "Il partit aussitôt sans rien réclamer", en: "He left at once without asking anything" },
        { fr: "Battus publiquement sans jugement, eux, citoyens romains — que les magistrats viennent eux-mêmes les libérer", en: "Beaten publicly without trial, they, Roman citizens — let the magistrates come free them in person" },
        { fr: "Il exigea une amende pour le tort subi", en: "He demanded a fine for the wrong suffered" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Après nous avoir battus de verges publiquement et sans jugement, nous qui sommes Romains, ils nous ont jetés en prison… qu'ils viennent eux-mêmes nous mettre en liberté. » (Actes 16:37) Paul n'invoque pas ses droits pour se venger, mais pour protéger la jeune église de Philippes (Ac 16:39-40).",
        en: "\"They have beaten us publicly without trial, men who are Romans, and have cast us into prison… let them come themselves and bring us out!\" (Acts 16:37) Paul invokes his rights not for revenge, but to protect the young church of Philippi (Acts 16:39-40).",
      },
    },
    {
      id: "qE2",
      level: "E",
      prompt: { fr: "Quel lien unit la nuit de la prison de Philippes et l'épître aux Philippiens ?", en: "What links the night in the Philippian jail with the letter to the Philippians?" },
      choices: [
        { fr: "Paul y reproche à la ville sa prison injuste", en: "In it Paul reproaches the city for his unjust imprisonment" },
        { fr: "L'épître ne parle jamais de joie", en: "The letter never speaks of joy" },
        { fr: "De nouveau prisonnier, Paul écrit à cette même ville : « Réjouissez-vous toujours dans le Seigneur »", en: "A prisoner once again, Paul writes to that same city: \"Rejoice in the Lord always\"" },
      ],
      answerIndex: 2,
      explain: {
        fr: "Des années après avoir chanté à minuit dans leur prison, Paul, de nouveau captif, écrit aux Philippiens : « Réjouissez-vous toujours dans le Seigneur; je le répète, réjouissez-vous. » (Philippiens 4:4) Ce qu'il avait vécu chez eux, il en fait un commandement d'espérance.",
        en: "Years after singing at midnight in their jail, Paul, captive once again, writes to the Philippians: \"Rejoice in the Lord always! Again I will say, 'Rejoice!'\" (Philippians 4:4) What he lived out among them he turns into a command of hope.",
      },
    },
    {
      id: "qD5",
      level: "D",
      prompt: { fr: "Quel jeune disciple Paul prit-il avec lui à Lystre, au début de ce voyage ?", en: "Which young disciple did Paul take with him at Lystra, at the start of this journey?" },
      choices: [
        { fr: "Timothée", en: "Timothy" },
        { fr: "Marc", en: "Mark" },
        { fr: "Apollos", en: "Apollos" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Il se rendit ensuite à Derbe et à Lystre. Et voici, il y avait là un disciple nommé Timothée. » (Actes 16:1) Recommandé par les frères, Timothée partit avec Paul (Ac 16:3) — le début d'un long compagnonnage de foi.",
        en: "\"He came to Derbe and Lystra: and behold, a certain disciple was there, named Timothy.\" (Acts 16:1) Well spoken of by the brothers, Timothy set out with Paul (Acts 16:3) — the beginning of a long companionship of faith.",
      },
    },
    {
      id: "qD6",
      level: "D",
      prompt: { fr: "Dans quelle région se trouvait la ville de Philippes ?", en: "In what region was the city of Philippi located?" },
      choices: [
        { fr: "En Galatie", en: "In Galatia" },
        { fr: "En Judée", en: "In Judea" },
        { fr: "En Macédoine", en: "In Macedonia" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« De là nous allâmes à Philippes, qui est la première ville d'un district de Macédoine, et une colonie. » (Actes 16:12) Philippes, colonie romaine de Macédoine, fut la première ville d'Europe à entendre l'Évangile.",
        en: "\"From there to Philippi, which is a city of Macedonia, the foremost of the district, a Roman colony.\" (Acts 16:12) Philippi, a Roman colony in Macedonia, was the first city in Europe to hear the Gospel.",
      },
    },
    {
      id: "qD7",
      level: "D",
      prompt: { fr: "Que criait la servante qui suivait Paul et ses compagnons ?", en: "What did the servant girl who followed Paul and his companions cry out?" },
      choices: [
        { fr: "« Chassez ces étrangers de la ville ! »", en: "\"Drive these strangers out of the city!\"" },
        { fr: "« Ces hommes sont les serviteurs du Dieu Très-Haut »", en: "\"These men are servants of the Most High God\"" },
        { fr: "« Grande est la déesse des Philippiens ! »", en: "\"Great is the goddess of the Philippians!\"" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Ces hommes sont les serviteurs du Dieu Très-Haut, et ils vous annoncent la voie du salut. » (Actes 16:17) Elle disait vrai, mais par un esprit de divination ; Paul, attristé, délivra la servante au nom de Jésus-Christ (Ac 16:18).",
        en: "\"These men are servants of the Most High God, who proclaim to us a way of salvation!\" (Acts 16:17) Her words were true, yet spoken by a spirit of divination; Paul, greatly annoyed, freed the girl in the name of Jesus Christ (Acts 16:18).",
      },
    },
    {
      id: "qA4",
      level: "A",
      prompt: { fr: "Par quelles étapes Paul arriva-t-il de Troas à Philippes ?", en: "By what stages did Paul travel from Troas to Philippi?" },
      choices: [
        { fr: "La Samothrace, puis Néapolis", en: "Samothrace, then Neapolis" },
        { fr: "Chypre, puis Athènes", en: "Cyprus, then Athens" },
        { fr: "Milet, puis Thessalonique", en: "Miletus, then Thessalonica" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Étant partis de Troas, nous fîmes voile directement vers la Samothrace, et le lendemain vers Néapolis. » (Actes 16:11) De ce port, la Via Egnatia montait vers Philippes : la géographie même servait la course de l'Évangile.",
        en: "\"Setting sail therefore from Troas, we made a straight course to Samothrace, and the day following to Neapolis.\" (Acts 16:11) From that port, the Via Egnatia climbed to Philippi: geography itself served the Gospel's advance.",
      },
    },
    {
      id: "qA5",
      level: "A",
      prompt: { fr: "Où Paul et Silas se rendirent-ils en sortant de la prison ?", en: "Where did Paul and Silas go when they came out of the prison?" },
      choices: [
        { fr: "Au tribunal des magistrats", en: "To the magistrates' court" },
        { fr: "Directement hors de la ville", en: "Straight out of the city" },
        { fr: "Chez Lydie, où ils encouragèrent les frères", en: "To Lydia's house, where they encouraged the brothers" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Quand ils furent sortis de la prison, ils entrèrent chez Lydie, et, après avoir vu et exhorté les frères, ils partirent. » (Actes 16:40) Avant de reprendre la route, ils fortifient la toute jeune église — née dans la maison de Lydie.",
        en: "\"They went out of the prison and entered into Lydia's house. When they had seen the brothers, they encouraged them, then departed.\" (Acts 16:40) Before taking the road again, they strengthen the newborn church — born in Lydia's home.",
      },
    },
    {
      id: "qA6",
      level: "A",
      prompt: { fr: "Avant la vision du Macédonien, où le Saint-Esprit empêcha-t-il Paul d'annoncer la parole ?", en: "Before the vision of the Macedonian, where did the Holy Spirit prevent Paul from speaking the word?" },
      choices: [
        { fr: "En Asie, puis en Bithynie", en: "In Asia, then in Bithynia" },
        { fr: "En Égypte, puis en Syrie", en: "In Egypt, then in Syria" },
        { fr: "En Crète, puis à Chypre", en: "In Crete, then in Cyprus" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Ayant été empêchés par le Saint-Esprit d'annoncer la parole dans l'Asie… l'Esprit de Jésus ne le leur permit pas » d'entrer en Bithynie (Actes 16:6-7). Deux portes fermées — pour que s'ouvre celle de la Macédoine (Ac 16:9-10) : Dieu dirige aussi par ses refus.",
        en: "\"Having been forbidden by the Holy Spirit to speak the word in Asia… the Spirit didn't allow them\" to enter Bithynia (Acts 16:6-7). Two closed doors — so that the door of Macedonia might open (Acts 16:9-10): God also guides through his refusals.",
      },
    },
    {
      id: "qE3",
      level: "E",
      prompt: { fr: "Que transmettaient Paul et ses compagnons aux églises, de ville en ville ?", en: "What did Paul and his companions deliver to the churches, from city to city?" },
      choices: [
        { fr: "Les lettres de recommandation des synagogues", en: "Letters of recommendation from the synagogues" },
        { fr: "Les décisions des apôtres et des anciens de Jérusalem", en: "The decrees of the apostles and elders at Jerusalem" },
        { fr: "L'argent de la collecte pour le temple", en: "The money collected for the temple" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« En passant par les villes, ils recommandaient aux frères d'observer les décisions des apôtres et des anciens de Jérusalem. » (Actes 16:4) Fruit du concile de Jérusalem (Ac 15), ces décisions affermissaient les églises, « qui croissaient en nombre de jour en jour » (Ac 16:5).",
        en: "\"As they went on their way through the cities, they delivered the decrees to them to keep which had been ordained by the apostles and elders who were at Jerusalem.\" (Acts 16:4) Fruit of the Jerusalem council (Acts 15), these decrees strengthened the churches, which \"increased in number daily\" (Acts 16:5).",
      },
    },
    {
      id: "qE4",
      level: "E",
      prompt: { fr: "Des années plus tard, qu'écrit Paul aux Philippiens au sujet de ses nouvelles chaînes ?", en: "Years later, what does Paul write to the Philippians about his new chains?" },
      choices: [
        { fr: "Qu'elles sont un châtiment à endurer en silence", en: "That they are a punishment to endure in silence" },
        { fr: "Qu'elles prouvent l'échec de sa mission", en: "That they prove the failure of his mission" },
        { fr: "Qu'elles ont plutôt contribué aux progrès de l'Évangile", en: "That they have rather turned out for the progress of the Good News" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Je veux que vous sachiez, frères, que ce qui m'est arrivé a plutôt contribué aux progrès de l'Évangile. » (Philippiens 1:12) Comme dans la nuit de Philippes, la captivité devient tribune : « mes liens sont devenus manifestes en Christ dans tout le prétoire » (Php 1:13).",
        en: "\"Now I desire to have you know, brothers, that the things which happened to me have turned out rather to the progress of the Good News.\" (Philippians 1:12) As in the night at Philippi, captivity becomes a pulpit: \"my bonds became evident in Christ throughout the whole palace guard\" (Php 1:13).",
      },
    },
    {
      id: "qE5",
      level: "E",
      prompt: { fr: "Qu'éprouvèrent les magistrats en apprenant que Paul et Silas étaient citoyens romains ?", en: "How did the magistrates react on learning that Paul and Silas were Roman citizens?" },
      choices: [
        { fr: "Ils furent effrayés et vinrent leur faire des excuses", en: "They were afraid and came to apologize to them" },
        { fr: "Ils les condamnèrent à une amende", en: "They sentenced them to a fine" },
        { fr: "Ils nièrent les avoir fait battre", en: "They denied having had them beaten" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Les licteurs rapportèrent ces paroles aux magistrats, qui furent effrayés en apprenant qu'ils étaient Romains. Ils vinrent les apaiser » et les prièrent de quitter la ville (Actes 16:38-39). Battre un citoyen romain sans jugement était un délit grave : la loi de Rome elle-même protégea les messagers de l'Évangile.",
        en: "\"The sergeants reported these words to the magistrates, and they were afraid when they heard that they were Romans, and they came and begged them\" and asked them to leave the city (Acts 16:38-39). Beating an uncondemned Roman citizen was a serious offense: Rome's own law protected the Gospel's messengers.",
      },
    },
    {
      id: "qA7",
      level: "A",
      prompt: { fr: "Après son baptême, quelle invitation Lydie adressa-t-elle à Paul et à ses compagnons ?", en: "After her baptism, what invitation did Lydia extend to Paul and his companions?" },
      choices: [
        { fr: "« Si vous me jugez fidèle au Seigneur, entrez dans ma maison, et demeurez-y »", en: "\"If you have judged me to be faithful to the Lord, come into my house and stay\"" },
        { fr: "« Priez pour ma maison, puis poursuivez votre route »", en: "\"Pray for my house, then continue on your way\"" },
        { fr: "« Revenez me voir au prochain sabbat »", en: "\"Come back to see me next Sabbath\"" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Lorsqu'elle eut été baptisée, avec sa famille, elle nous fit cette demande: Si vous me jugez fidèle au Seigneur, entrez dans ma maison, et demeurez-y. Et elle nous pressa par ses instances. » (Actes 16:15) Un cœur ouvert devient une maison ouverte : c'est chez Lydie que se retrouvera la toute jeune église de Philippes (Ac 16:40).",
        en: "\"When she and her household were baptized, she begged us, saying, 'If you have judged me to be faithful to the Lord, come into my house and stay.' So she persuaded us.\" (Acts 16:15) An opened heart becomes an open home: it is at Lydia's house that the newborn church of Philippi would gather (Acts 16:40).",
      },
    },
    {
      id: "qE6",
      level: "E",
      prompt: { fr: "Quel changement dans le récit des Actes montre que Luc rejoignit l'équipe missionnaire vers Troas ?", en: "What change in the narrative of Acts shows that Luke joined the missionary team near Troas?" },
      choices: [
        { fr: "Le récit cite désormais des dates précises", en: "The narrative now gives precise dates" },
        { fr: "Luc signe chaque chapitre de son nom", en: "Luke signs each chapter with his name" },
        { fr: "Le récit passe de « ils » à « nous » : « nous cherchâmes aussitôt à nous rendre en Macédoine »", en: "The narrative shifts from 'they' to 'we': 'immediately we sought to go out to Macedonia'" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Après cette vision de Paul, nous cherchâmes aussitôt à nous rendre en Macédoine. » (Actes 16:10) C'est le premier passage en « nous » du livre des Actes : Luc, l'auteur, vient de se joindre à Paul, Silas et Timothée — témoin oculaire de l'entrée de l'Évangile en Europe et de la nuit de Philippes.",
        en: "\"When he had seen the vision, immediately we sought to go out to Macedonia.\" (Acts 16:10) This is the first 'we' passage of the book of Acts: Luke, the author, has just joined Paul, Silas and Timothy — an eyewitness of the Gospel's entry into Europe and of the night at Philippi.",
      },
    },
  ],
  tips: [
    {
      fr: "Maintiens la louange sans lâcher : Paul et Silas chantaient à minuit, dans les chaînes (Ac 16:25). C'est la louange tenue qui déclenche le séisme.",
      en: "Hold the praise without letting go: Paul and Silas sang at midnight, in chains (Acts 16:25). It is sustained praise that triggers the earthquake.",
    },
    {
      fr: "Après le séisme, ramasse les huit chaînes rompues et les deux ceps brisés — « les liens de tous les prisonniers furent rompus » (Ac 16:26).",
      en: "After the earthquake, gather the eight broken chains and the two split stocks — \"everyone's bonds were loosened\" (Acts 16:26).",
    },
    {
      fr: "Le geôlier a demandé de la lumière (Ac 16:29) : porte-lui les quatre lampes, puis parle-lui.",
      en: "The jailer called for lights (Acts 16:29): carry the four lamps to him, then speak with him.",
    },
    {
      fr: "Quand les portes s'ouvrent, ne fuis pas — reste, comme Paul (Ac 16:28), et le geôlier trouvera le salut.",
      en: "When the doors open, do not flee — stay, like Paul (Acts 16:28), and the jailer will find salvation.",
    },
    {
      fr: "Les cinq fragments du verset Actes 16:31 sont dispersés dans toute la ville : rassemble-les pour recomposer la promesse entière.",
      en: "The five fragments of the verse Acts 16:31 are scattered across the whole town: gather them to piece the full promise back together.",
    },
    {
      fr: "Va jusqu'à la rivière au sud-ouest : Lydie attend ses étoffes de pourpre, et le gué du baptême attend la famille du geôlier (Ac 16:14, 33).",
      en: "Go down to the river in the south-west: Lydia is waiting for her purple cloths, and the baptism ford awaits the jailer's household (Acts 16:14, 33).",
    },
  ],
  lesson: {
    fr: "Vers le milieu de la nuit, Paul et Silas priaient et chantaient les louanges de Dieu. (Actes 16:25)",
    en: "But about midnight Paul and Silas were praying and singing hymns to God. (Acts 16:25)",
  },
  audio: { ambience: "city" },
  multiplayer: { mode: "coop", maxPlayers: 4 },
};

export default voyagesDePaul;
